import 'virtual:uno.css';
import '@console/design-system/tokens/generated/tokens.css';
import PortalVue from 'portal-vue';
import { createApp, defineComponent, ref, watch } from 'vue';
import { RouteRecordRaw, useRoute } from 'vue-router';
import { useCoffer } from '@console/composables/useCoffer';
import { loadLaunchDarkly } from '@console/composables/useConfig';
import { useProducts } from '@console/composables/useProducts';
import { useUser } from '@console/composables/useUser';
import { useTrackers } from '@console/composables/useTrackers';
import { getVuetify } from '@console/core/vuetify';
import { queryClient } from '@console/query';
import { router } from '@console/router';
import { VueQueryPlugin } from '@tanstack/vue-query';
// import '../setup';
import ConsoleLoader from './ConsoleLoader.vue';
import { initSentry } from './initSentry';
import { setupNinja } from '@console/core/ninja';
import { watchFaviconColorScheme } from './watchFaviconColorScheme';

// Unregister any existing service workers to make sure that old cached versions are not used
async function unregisterServiceWorkers() {
  if (!navigator?.serviceWorker) {
    return;
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  registrations.forEach(reg => reg.unregister());
}

/**
 * Create Console Vue app with all bindings.
 * Requires a root component and set of routes for the app
 */
export function createConsoleApp(routes: RouteRecordRaw[], App: any, productId = '', customLoaders: Promise<any>[] = []) {
  void unregisterServiceWorkers();
  watchFaviconColorScheme();

  // add app routes
  routes.forEach(route => router.addRoute(route));

  const ConsoleRoot = defineComponent({
    name: 'ConsoleRoot',

    setup() {
      const { currentProductId } = useProducts();
      currentProductId.value = productId;

      return () => <App theme={'core'} product-id={productId} />;
    },
  });

  const { isLogged, userInfo } = useUser();
  const { loadCofferData } = useCoffer();
  const { loadProductsDefinition } = useProducts();
  const { loadAllTrackers } = useTrackers();

  const app = createApp({
    setup() {
      const isLoading = ref(true);

      const route = useRoute();

      watch(
        isLogged,
        async value => {
          if (value) {
            isLoading.value = true;

            if (route.name === 'sso') {
              // The first login action happens on SSO, but immediately we fully reload the page,
              // so that there is no point to load any additional data as we do it again once the page is reloaded
              await loadProductsDefinition(true);
              return;
            } else {
              setupNinja();
              await loadLaunchDarkly(productId, userInfo.value);
              await Promise.allSettled([loadProductsDefinition(true), loadCofferData(), loadAllTrackers(), ...customLoaders]);
            }
          }
          isLoading.value = false;
        },
        { immediate: true }
      );

      return () => {
        if (isLoading.value) {
          return <ConsoleLoader />;
        }
        return <ConsoleRoot />;
      };
    },
  });

  initSentry(app);
  // initLaunchDarkly(app);

  app.use(getVuetify('core'));
  app.use(router);
  app.use(PortalVue);
  // Must be installed before mount, or its `provide` never reaches components that have
  // already run `setup()`.
  app.use(VueQueryPlugin, { queryClient });

  app.mount('#app');

  return app;
}
